import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import botblogrLogo from "../assets/images/botblogrLogo.png";
import {
  useGetAllProjectsQuery,
  useSelectProjectMutation,
} from "../redux/slices/projectApiSlice";
import { setSelectedProject } from "../redux/slices/projectSlice";
import sideNavData from "./nav";
import Header from "./Header";
import "../../scss/sidebar/sidebar.scss";

export const Sidebar = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [openTools, setOpenTools] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state?.auth).userInfo;
  const dispatch = useDispatch();

  // Fetch projects and mutation to select the project
  const { data: projects, refetch } = useGetAllProjectsQuery();
  const [selectProject, { isLoading: selectLoading }] =
    useSelectProjectMutation();

  const isQueryParams = (value) => {
    if (location?.search) {
      return `${location.pathname}${location.search}` === value ? "active" : "";
    } else {
      return isActive(value);
    }
  };

  const isActive = (value) => (location?.pathname === value ? "active" : "");

  const handleDrawer = () => {
    setOpen(!open);
  };

  const handleMainToolsClick = () => {
    setOpenTools(true);
  };

  const handleProjectChange = async (selectedProject) => {
    try {
      const id = selectedProject?.value ? selectedProject.value : 0;
      const { data } = await selectProject({ id: id });
      dispatch(setSelectedProject(data.data));
      refetch();
    } catch (error) {
      console.error("Error selecting project:", error);
    }
  };

  const renderSubMenu = (children) => {
    return (
      <>
        {children.map((child, childIndex) => (
          <li
            className={`flex items-center ${
              isQueryParams(child?.link_to) ? "bg-blue-50" : "bg-gray"
            } justify-center hover:bg-blue-50 py-2`}
            key={childIndex}
            onClick={() => navigate(`${child.link_to}`, { replace: true })}
          >
            <button className="px-6 text-white w-full py-2 flex item-center gap-3 md:ml-6 ml-0">
              {child.icon} {t(child.name)}
            </button>
          </li>
        ))}
      </>
    );
  };

  // Format projects for React-Select options
  const projectOptions = [
    {
      value: null, // This will send `null` when selected
      label: (
        <div className="flex items-center gap-2">
          <div className="font-bold">{t("Unselect", "Désélectionner")}</div>
        </div>
      ),
    },
    ...(projects?.data?.map((project) => ({
      value: project.id,
      label: (
        <div className="flex items-center gap-2">
          <img src={project.favicon} alt={project.name} className="w-6 h-6" />
          <div className="flex-1 min-w-0">
            <div className="font-bold">{project.name}</div>
            <div
              className="text-xs text-gray-500 truncate overflow-hidden whitespace-nowrap"
              title={project.url} // Tooltip for full URL
            >
              {project.url}
            </div>
          </div>
        </div>
      ),
    })) || []), // Fallback to an empty array if projects or data is undefined
  ];

  const currentProject = projects?.data?.find((project) => project.current);
  useEffect(() => {
    if (currentProject) {
      dispatch(setSelectedProject(currentProject));
    }
  }, [currentProject, dispatch]);

  return (
    <>
      <div className="flex md:h-screen h-full">
        <aside
          className={`absolute left-0 top-0 z-50 flex h-screen w-72 flex-col overflow-y-visible bg-darkgray duration-300 ease-linear dark:bg-boxdark lg:static lg:translate-x-0 ${
            open ? "-translate-x-full" : null
          }`}
        >
          <div className="h-full py-4 overflow-y-auto bg-gray-50">
            <Link
              to={"/dashboard"}
              className="flex items-center sm:space-x-3 rtl:space-x-reverse mb-4 pl-2"
            >
              <img
                src={botblogrLogo}
                className="w-[176px] md:ml-6 ml-0  object-contain"
              />
            </Link>

            <ul className="font-medium sidbar_menu">
              {sideNavData?.map((item, index) =>
                (item?.name !== "Main Tools" && item?.name !== "Error-logs") ||
                (item?.name === "Error-logs" && userInfo.role === "admin") ? (
                  <li
                    key={index}
                    className={`items-center justify-center ${
                      isActive(item?.link_to) ? "bg-blue-50" : null
                    }`}
                    onClick={() => {
                      if (item?.children) {
                        handleMainToolsClick(); // Toggle Main Tools submenu
                      } else {
                        setOpenTools(false);
                        navigate(`${item?.link_to}`, { replace: true });
                      }
                    }}
                  >
                    <button className="px-4 text-white w-full py-4 flex item-center justify-between">
                      <span className="lex item-center gap-3">
                        {item?.icon} {t(item?.name)}
                      </span>
                      {item?.children && (
                        <>
                          {openTools ? (
                            <ExpandLessIcon
                              style={{ color: "white", fontSize: "20px" }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              style={{ color: "white", fontSize: "20px" }}
                            />
                          )}
                        </>
                      )}
                    </button>
                    {item?.children && (
                      <ul className="">
                        {item?.children &&
                          openTools &&
                          renderSubMenu(item?.children)}
                      </ul>
                    )}
                  </li>
                ) : null
              )}
            </ul>

            {/* Project Selector */}
            <div className="px-4 py-4">
              <ReactSelect
                options={projectOptions}
                value={
                  currentProject
                    ? {
                        value: currentProject.id,
                        label: (
                          <div className="flex items-center gap-2">
                            <img
                              src={currentProject.favicon}
                              alt={currentProject.name}
                              className="w-6 h-6"
                            />
                            <div className="flex-1 min-w-0">
                              {" "}
                              {/* Dynamic width container */}
                              <div className="font-bold">
                                {currentProject.name}
                              </div>
                              <div
                                className="text-xs text-gray-500 truncate" // Truncates text dynamically
                                title={currentProject.url} // Shows full URL on hover
                              >
                                {currentProject.url}
                              </div>
                            </div>
                          </div>
                        ),
                      }
                    : null
                }
                styles={{
                  menu: (base) => ({
                    ...base,
                    maxHeight: "464px",
                    overflowY: "auto",
                    zIndex: 9999,
                  }),
                }}
                menuPlacement="top"
                onChange={handleProjectChange}
                isSearchable={false}
                isDisabled={selectLoading}
                placeholder={t("Select-a-project", "Sélectionner un projet...")}
              />
            </div>
          </div>

          <div className="lg:hidden block">
            <ArrowBackIcon
              onClick={handleDrawer}
              sx={{
                color: "#fff",
                position: "absolute",
                top: "20px",
                right: "20px",
                rotate: "180deg",
              }}
            />
          </div>
          <div className="text-white text-center py-4">
            <p>Des questions ?</p>
            <a href="mailto:contact@botblogr.com" className="font-bold">
              contact@botblogr.com
            </a>
          </div>
        </aside>
        <Header handleDrawer={handleDrawer} />
      </div>
    </>
  );
};

export default Sidebar;
